import { RefObject, useEffect, useId, useRef, useState } from 'react';
import { classNames, getAbortController, getAllFocusableElements, getScrollableParents, isEscape, isEvent, isEventTargetWithin, isNavigationEvent, isNavigationEventHorizantal, isNavigationEventVertical, resetOnosition, useResetState } from '../util/helpers';
import { PopoverPositionType, PopoverSpacingType } from './Popover';
import updatePosition, { LoopThroughThePositions, getPositonsOnDirection, getSecondLast, isColidingWithViewPort, isElementOutofView, updateBasePosition } from './updatePosition.helpers';
import { KEY_MAPS } from '../util/_event.map';

export type UsePopoverReturnType<T> = {
    anchorProps: React.HtmlHTMLAttributes<T>;
    popoverProps: React.HtmlHTMLAttributes<HTMLElement>;
    anchorRef: React.MutableRefObject<T | HTMLElement | null>;
    popoverRef: React.MutableRefObject<HTMLElement | null>;
    focusAndClose: () => void;
    close: () => void;
}
export type DirectionType = {
    top?: PopoverPositionType[],
    left?: PopoverPositionType[],
    bottom?: PopoverPositionType[],
    right?: PopoverPositionType[],
}
export type LivePositionType = {
    preferredPosition: PopoverPositionType,
    actualPosition: PopoverPositionType,
    directions: DirectionType
}

export type UsePopoverProps = {
    isOpen: boolean;
    /** It should be a state update function */
    setIsOpen: (v: boolean) => void;
    anchorId?: string;
    popoverId?: string;
    popoverClassName?: string;
    preferredPosition?: PopoverPositionType;
    spacingType?: PopoverSpacingType;
    trapFocus?: boolean;
    /** The Popover wii be closed if click outside the popover */
    closeOnClickOutside?: boolean;
    /** The Popover wii be closed if the focus is out of anchor or popover */
    closeOnFocusOut?: boolean;
    /** The Popover will hide if the anchor is out of view */
    hideOutOfView?: boolean;
    enableVerticalNavigation?: boolean;
    enableHorizantalNavigation?: boolean;
    popoverAriaLabel?: string;
    autoPositionOnCollision?: boolean;
}
/**
 * Use popover hook to be used for any popover element
 */
export default function usePopover<T>(props: UsePopoverProps): UsePopoverReturnType<T> {
    const {
        isOpen,
        preferredPosition = 'bottom-left',
        setIsOpen,
        anchorId,
        popoverId,
        spacingType = 'default',
        popoverClassName,
        trapFocus = true,
        closeOnClickOutside = true,
        enableVerticalNavigation = false,
        enableHorizantalNavigation = false,
        popoverAriaLabel,
        autoPositionOnCollision = true,
        closeOnFocusOut = false,
        hideOutOfView = false
    } = props;

    const anchorRef = useRef<T | HTMLElement | null>(null);
    const popoverRef = useRef<HTMLElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [actualPosition, setActualPosition] = useState(preferredPosition);
    const [oldPositions, setOldPosition] = useState<PopoverPositionType[]>([preferredPosition]);
    const [hideOnViewPortOverlap, setHideOnViewPortOverlap] = useState(false);
    const [focusIndex, setFocusIndex] = useResetState<any>(-1);
    const offset = 8;

    const focusableElements = useRef<HTMLElement[]>([]);
    const scrollableParents = useRef<HTMLElement[]>([]);

    const updatePopoverPosition = () => {
        let parent;

        if (scrollableParents.current.length > 0) {
            parent = scrollableParents.current[0];
        }

        const outOfview = isElementOutofView(anchorRef.current as any, parent);
        /** [DO-REMOVE] Keeping for some refference*/
        // if (!isOpen || hideOnViewPortOverlap) {
        //     updateBasePosition();
        // }

        if (outOfview && hideOutOfView) {
            return setHideOnViewPortOverlap(true);
        }

        if (isOpen && popoverRef.current && anchorRef.current) {
            const actualDirection = actualPosition.split('-')[0];

            const { isColliding } = isColidingWithViewPort(popoverRef.current, parent);

            let prefferedPopoverPosition = actualPosition;

            const pos = getPositonsOnDirection(anchorRef.current as HTMLElement, popoverRef.current, parent);
            let opositions = [...oldPositions];
            const lastPosition = getSecondLast(opositions);

            if (lastPosition && pos?.avalilablePositions.includes(lastPosition)) {
                prefferedPopoverPosition = lastPosition;
                opositions = resetOnosition(opositions, lastPosition);
            }
            else if (!pos?.avalilablePositions.includes(prefferedPopoverPosition) && isColliding && autoPositionOnCollision) {
                prefferedPopoverPosition = LoopThroughThePositions(actualDirection as any, pos?.avalilablePositions as PopoverPositionType[], prefferedPopoverPosition);
            }

            const cp = updatePosition(anchorRef as RefObject<HTMLElement>, popoverRef, prefferedPopoverPosition, offset);

            if (cp) {
                setPosition(cp);
                setHideOnViewPortOverlap(false);
                setActualPosition(prefferedPopoverPosition);
                if (!opositions.includes(prefferedPopoverPosition)) {
                    opositions.push(prefferedPopoverPosition);
                }
                setOldPosition(opositions);
            }
        }
    }

    const onActionClick = (event: any) => {
        if (anchorRef.current === null && popoverRef.current === null) return;
        if (closeOnClickOutside && (!isEventTargetWithin(event, popoverRef.current) && anchorRef.current !== event.target)) {
            setIsOpen(false);
        }
        return;
    }

    const loopThroughFocusableElement = (event: any) => {
        let currentIndex: any = -1;
        const elements = focusableElements.current;

        for (let i = 0; i < elements.length; i++) {
            const item = elements[i];
            if (item.isSameNode(document.activeElement)) {
                currentIndex = i;
                break;
            }
        }

        const lastIndex = elements.length - 1;
        let focusableIndex = 0;

        if (isEvent(event, KEY_MAPS.ArrowDown) || isEvent(event, KEY_MAPS.ArrowRight)) {
            focusableIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
        }
        else if (isEvent(event, KEY_MAPS.ArrowUp) || isEvent(event, KEY_MAPS.ArrowLeft)) {
            focusableIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
        }

        elements[focusableIndex].focus();
    }

    const onKeboardAction = (event: any) => {
        if (isEscape(event)) {
            if (isEventTargetWithin(event, popoverRef.current)
                || (anchorRef.current as HTMLElement).isSameNode(event.target)) {
                (anchorRef.current as unknown as HTMLElement)?.focus();
            }
            return setIsOpen(false)
        }
        if ((popoverRef.current && isEventTargetWithin(event, popoverRef.current))) {
            if (focusableElements.current.length > 0) {
                if (trapFocus && isNavigationEvent(event)) {
                    if (((enableHorizantalNavigation && isNavigationEventHorizantal(event))
                        || (enableVerticalNavigation && isNavigationEventVertical(event)))) {

                        loopThroughFocusableElement(event);
                        event.preventDefault();
                    }
                    else if (isEvent(event, KEY_MAPS.Tab)) {
                        const lastItem = focusableElements.current[focusableElements.current.length - 1];
                        if (lastItem.isSameNode(document.activeElement) && event.shiftKey === false) {
                            event.preventDefault();
                            focusableElements.current[0].focus();
                        }
                    }

                } else if (isEvent(event, KEY_MAPS.Home)) {
                    focusableElements.current[0].focus();
                } else if (isEvent(event, KEY_MAPS.End)) {
                    focusableElements.current[focusableElements.current.length - 1].focus();
                }
            }
        }
    }

    //Update the actualPosition when prefferedPosition changed through props
    useEffect(() => {
        setActualPosition(preferredPosition);
        setOldPosition([preferredPosition]);
    }, [preferredPosition]);

    // Place the popover in the right place when opened
    useEffect(() => {
        const { controller, signal } = getAbortController();
        if (isOpen) {
            updatePopoverPosition();
        } else {
            setFocusIndex();
        }

        // Find scrollable parents
        scrollableParents.current = getScrollableParents(anchorRef.current as HTMLElement)

        // Attach scroll event
        scrollableParents.current.forEach(parent => parent.addEventListener('scroll', updatePopoverPosition), signal);

        window.addEventListener('scroll', updatePopoverPosition, signal);
        window.addEventListener('resize', updatePopoverPosition, signal);

        return () => {
            controller.abort();
        }

    }, [isOpen, actualPosition, popoverRef.current, spacingType, autoPositionOnCollision]);

    // Close popover on Focus out effect
    useEffect(() => {
        const { controller, signal } = getAbortController();

        const onFocusOut = (event: any) => {
            if (!(popoverRef.current?.contains(event.relatedTarget as Node) || (anchorRef.current as HTMLElement).isSameNode(event.relatedTarget))) {
                setIsOpen(false);
            }
        }

        if (popoverRef.current) {
            if (isOpen && closeOnFocusOut) {
                popoverRef.current?.addEventListener('focusout', onFocusOut, signal);
            }
        }


        const onFocusAnchor = () => {
            setFocusIndex();
        }

        if (anchorRef.current) {
            (anchorRef.current as HTMLElement)?.addEventListener('focus', onFocusAnchor, signal);
        }

        return () => {
            controller.abort();
        }
    }, [popoverRef.current, closeOnFocusOut, anchorRef.current]);

    // Close on Click outside effect
    useEffect(() => {
        const { controller, signal } = getAbortController();

        const onFocus = (index: number) => {
            return () => {
                setFocusIndex(index);
            }
        }

        if (popoverRef.current)
            focusableElements.current = Array.from(getAllFocusableElements(popoverRef.current) as NodeListOf<HTMLElement>)
        else focusableElements.current = [];

        if (!hideOnViewPortOverlap && focusIndex >= 0) {
            focusableElements.current.forEach((element: HTMLElement, index: number) => {
                if (index === focusIndex) {
                    element.focus({
                        focusVisible: true
                    } as any);
                }
            })
        }

        if (!isOpen) {
            setFocusIndex();
        }

        focusableElements.current.forEach((element: HTMLElement, index: number) => {
            element.addEventListener('focus', onFocus(index), signal);
        });
        document.addEventListener('keydown', onKeboardAction, signal);
        document.addEventListener('click', onActionClick, signal);

        return () => {
            controller.abort();
        }
    }, [trapFocus, closeOnClickOutside, popoverRef.current, hideOnViewPortOverlap, isOpen]);

    useEffect(() => {
        if (anchorRef.current && isOpen === false) {
            const pos = updateBasePosition(anchorRef as any, preferredPosition);
            setPosition(pos);
        }
    }, [anchorRef]);

    const animateClass = classNames(
        'ted-ui-popover',
        preferredPosition && `ted-ui-popover-${preferredPosition}`,
        preferredPosition && `ted-ui-po-animate-${actualPosition.split('-')[0]}`,
        spacingType && `ted-ui-popover-spacing-${spacingType}`,
        hideOnViewPortOverlap && `ted-ui-popover-hide`
    );

    const targetId = anchorId ?? `${useId()}-po-anchor`;
    const popoverElementId = popoverId ?? `${useId()}-po-content`;

    const pba = {
        popovertarget: popoverElementId
    }

    const anchorProps: React.HTMLProps<T> = {
        id: targetId,
        "aria-controls": isOpen ? popoverElementId : undefined,
        "aria-expanded": isOpen,
        ...pba
    }

    const pa = {
        popover: "true"
    }

    const focusAndClose = () => {
        if (anchorRef.current) {
            (anchorRef.current as HTMLElement).focus();
        }

        console.log("TEST")

        setIsOpen(false);
    }

    const close = () => {
        setIsOpen(false);
    }

    const popoverAriaProps = popoverAriaLabel ? { 'aria-label': popoverAriaLabel } : { "aria-labelledby": targetId }

    const popoverProps: React.HTMLProps<HTMLElement> = {
        style: {
            ...position,
            position: 'absolute'
        },
        tabIndex: -1,
        role: 'region',
        id: popoverElementId,
        className: classNames(animateClass, popoverClassName),
        ...popoverAriaProps,
        ...pa
    }

    return ({
        anchorRef,
        popoverRef,
        anchorProps,
        popoverProps,
        focusAndClose,
        close
    });
}
